import { curry, pick, prop } from 'ramda'
import { createSelector } from 'redux-bundler'

import getGlobal from '~/src/Lib/getGlobal'
import { debounce, defer, deferred, deferUntil, EMPTY_OBJECT, shallowEquals } from '~/src/Lib/Utils'

export const DIMENSIONS_UPDATED = 'DIMENSIONS_UPDATED'
export const DIMENSIONS_ORIENTATION_PORTRAIT = 'DIMENSIONS_ORIENTATION_PORTRAIT'
export const DIMENSIONS_ORIENTATION_LANDSCAPE = 'DIMENSIONS_ORIENTATION_LANDSCAPE'

const CONTENT_HEIGHT_SUBTRAHENDS = ['appBar', 'prodBanner', 'versionBanner']
const CONTENT_WIDTH_SUBTRAHENDS = ['navRail']
const global = getGlobal()
const { document } = global
const getScrollParent = () => document?.querySelector('main#content')
const calcOrientation = (height = 0, width = 0) =>
  height > width ? DIMENSIONS_ORIENTATION_PORTRAIT : DIMENSIONS_ORIENTATION_LANDSCAPE

const trackWidth = (initial = { window: global.innerWidth, content: global.innerWidth }) => {
  let payload = initial
  const tracked = document?.querySelectorAll('[data-trackwidth]') ?? []

  const { content: oldContent, window: oldWindow, ...oldTracked } = payload
  if (oldWindow !== global.innerWidth) {
    payload = { ...payload, window: global.innerWidth }
  }
  if (!tracked.length) return payload

  const fullRefresh = Object.keys(oldTracked).length !== tracked.length

  payload = Array.from(tracked).reduce(
    (acc, el) => {
      if (!fullRefresh && !el.dataset.trackwidth) return acc
      const { [el.dataset.trackwidth]: oldWidth } = acc
      const newWidth = el.getBoundingClientRect().width
      if (oldWidth === newWidth) return acc
      return { ...acc, [el.dataset.trackwidth]: newWidth }
    },
    fullRefresh ? {} : payload,
  )

  const content =
    payload.window < 960 || document?.querySelector('#wrapper.fullscreen')
      ? payload.window
      : payload.window - CONTENT_WIDTH_SUBTRAHENDS.reduce((acc, key) => acc + (payload[key] || 0), 0)

  if (oldContent !== content) {
    payload = { ...payload, content }
  }

  return payload
}

const trackHeight = (initial = { window: global.innerHeight, content: global.innerHeight }) => {
  let payload = initial
  const tracked = document?.querySelectorAll('[data-trackheight]')

  const { content: oldContent, window: oldWindow, ...oldTracked } = payload
  if (oldWindow !== global.innerHeight) {
    payload = { ...payload, window: global.innerHeight }
  }

  if (!tracked?.length) return payload

  const fullRefresh = Object.keys(oldTracked).length !== tracked.length

  payload = Array.from(tracked).reduce(
    (acc, el) => {
      if (!el.dataset.trackheight) return acc
      const { [el.dataset.trackheight]: oldHeight } = acc
      const newHeight = el.getBoundingClientRect().height

      if (oldHeight === newHeight) return acc

      return {
        ...acc,
        [el.dataset.trackheight]: newHeight,
      }
    },
    fullRefresh ? {} : payload,
  )

  const content = payload.window - CONTENT_HEIGHT_SUBTRAHENDS.reduce((acc, key) => acc + (payload[key] || 0), 0)

  if (oldContent !== content) {
    payload = { ...payload, content }
  }

  return payload
}

const trackScroll = () => {
  const scrollParent = getScrollParent()

  return { x: scrollParent?.scrollLeft, y: scrollParent?.scrollTop }
}

const trackOrientation = (w = global) => calcOrientation(w.innerHeight, w.innerWidth)

const dispatcher = debounce(
  (dispatch, payload) =>
    dispatch({
      type: DIMENSIONS_UPDATED,
      payload,
    }),
  250,
)

const defaultState = {
  width: trackWidth(),
  height: trackHeight(),
  orientation: trackOrientation(),
  scroll: trackScroll(),
}

const isDimensionsPayload = payload => payload && Object.keys(defaultState).some(key => key in payload)

export default {
  name: 'dimensions',
  getMiddleware: () => {
    let timeout
    return curry((store, next, action) => {
      if (typeof action?.payload?.url !== 'string') {
        next(action)
        return
      }
      if (timeout) {
        clearTimeout(timeout)
      }
      const { url: prevUrl } = store.selectRouteInfo()
      const {
        payload: { url },
      } = action

      const [, suffix] = url.split(location.host)
      const [path] = suffix.split('?')
      if (path !== prevUrl) {
        timeout = defer(() => {
          timeout = null
          dispatcher(store.dispatch, {
            width: trackWidth(store.selectWidths()),
            height: trackHeight(store.selectHeights()),
            orientation: trackOrientation(),
          })
        }, 500)
      }
      next(action)
    })
  },
  init: store => {
    if (!global.addEventListener) {
      return undefined
    }
    const resizeHandler = debounce(() => {
      store.dispatch({
        type: DIMENSIONS_UPDATED,
        payload: {
          width: trackWidth(store.selectWidths()),
          height: trackHeight(store.selectHeights()),
          orientation: trackOrientation(),
        },
      })
    }, 250)
    const deferredResizeHandler = deferred(resizeHandler, 1000)

    global?.addEventListener('resize', resizeHandler)
    global?.addEventListener('orientationchange', deferredResizeHandler)

    const scrollHandler = debounce(() => {
      store.dispatch({
        type: DIMENSIONS_UPDATED,
        payload: {
          scroll: trackScroll(),
        },
      })
    }, 150)

    deferUntil(
      () => {
        getScrollParent()?.addEventListener('scroll', scrollHandler)
        scrollHandler()
      },
      getScrollParent,
      33,
    )

    return () => {
      global.removeEventListener('resize', resizeHandler)
      global.removeEventListener('orientationchange', deferredResizeHandler)
      getScrollParent()?.removeEventListener('scroll', scrollHandler)
    }
  },
  reducer: (state = defaultState, action = {}) => {
    if (action.type === DIMENSIONS_UPDATED) {
      const changed = Object.keys(action.payload).filter(key => !shallowEquals(state[key], action.payload[key]))

      if (changed.length > 0) {
        return {
          ...state,
          ...pick(changed, action.payload),
        }
      }
    }
    return state
  },
  doUpdateDimensions:
    payload =>
    ({ dispatch, store }) =>
      dispatcher(
        dispatch,
        isDimensionsPayload(payload)
          ? payload
          : {
              width: trackWidth(store.selectWidths()),
              height: trackHeight(store.selectHeights()),
              orientation: trackOrientation(),
            },
      ),
  doUpdateHeights:
    () =>
    ({ dispatch, store }) =>
      dispatcher(dispatch, {
        height: trackHeight(store.selectHeights()),
      }),
  doUpdateWidths:
    () =>
    ({ dispatch, store }) =>
      dispatcher(dispatch, {
        width: trackWidth(store.selectWidths()),
      }),
  doUpdateOrientation:
    () =>
    ({ dispatch }) =>
      dispatcher(dispatch, {
        orientation: trackOrientation(),
      }),
  selectDimensions: state => state.dimensions,
  selectWidths: createSelector('selectDimensions', prop('width')),
  selectHeights: createSelector('selectDimensions', prop('height')),
  selectOrientation: createSelector('selectDimensions', prop('orientation')),
  selectIsLandscape: createSelector(
    'selectOrientation',
    orientation => orientation === DIMENSIONS_ORIENTATION_LANDSCAPE,
  ),
  selectIsPortrait: createSelector('selectOrientation', orientation => orientation === DIMENSIONS_ORIENTATION_PORTRAIT),
  selectIsMobile: createSelector(
    'selectWidths',
    'selectHeights',
    'selectIsLandscape',
    ({ window: windowWidth }, { window: windowHeight }, isLandscape) =>
      isLandscape ? windowWidth < 960 && windowHeight < 500 : windowWidth < 500 && windowHeight < 960,
  ),
  reactUpdateDimensions: createSelector('selectDimensions', dimensions => {
    const { width, height, orientation } = dimensions

    let payload = EMPTY_OBJECT
    const newWidth = trackWidth(width)
    if (newWidth !== width) {
      payload = { ...payload, width: newWidth }
    }

    const newHeight = trackHeight(height)
    if (newHeight !== height) {
      payload = { ...payload, height: newHeight }
    }

    const newOrientation = trackOrientation()
    if (newOrientation !== orientation) {
      payload = { ...payload, orientation: newOrientation }
    }

    if (payload !== EMPTY_OBJECT) {
      return { actionCreator: 'doUpdateDimensions', args: [payload] }
    }
    return undefined
  }),
}
